import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarX, PackageX, PauseCircle, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { isPartnerBrand, useBrand } from "@/shared/brand";
import { useSubscriptionLapse } from "@/features/billing/model/useSubscriptionLapse";
import { lapseNoticeText } from "@/features/billing/lib/subscriptionLapse";
import {
  FORBIDDEN_REASON_MODULE_EXPIRED,
  FORBIDDEN_REASON_MODULE_NOT_ENABLED,
  FORBIDDEN_REASON_MODULE_PAUSED,
  FORBIDDEN_REASON_PARAM,
} from "@/shared/lib/forbiddenReason";

export default function Forbidden() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // 0926 修复波 #10:租户未开通(module 门)与角色无权是两回事,前者不能说「你的角色无权访问」。
  const reason = searchParams.get(FORBIDDEN_REASON_PARAM);
  const notEnabled = reason === FORBIDDEN_REASON_MODULE_NOT_ENABLED;
  // OEM 第二批 C4:已拥有但被服务商暂停(后端 reason=PAUSED)—— 不能说「还没开通,联系我们开通」。
  const paused = reason === FORBIDDEN_REASON_MODULE_PAUSED;
  // 1005:拥有过、因到期关闭 —— 说「已到期」+ 续费找谁 + 去我的作品(历史作品仍可看可下载)
  const expired = reason === FORBIDDEN_REASON_MODULE_EXPIRED;

  // L1 修复:未登录直接跳 login,避免显示"未登录"占位 + "返回工作台"按钮触发
  // / → ProtectedRoute → /403 的 redirect loop
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <div className="max-w-md text-center space-y-4 fx-enter">
        {expired ? (
          <ExpiredNotice />
        ) : paused ? (
          <>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-warning/10">
              <PauseCircle className="h-8 w-8 text-warning" />
            </div>
            <h1 className="text-2xl font-bold">{t("pages.forbidden.pausedTitle")}</h1>
            <p className="text-base text-muted-foreground">{t("pages.forbidden.pausedNotice")}</p>
          </>
        ) : notEnabled ? (
          <>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-muted-foreground/10">
              <PackageX className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold">{t("pages.forbidden.notEnabledTitle")}</h1>
            <p className="text-base text-muted-foreground">
              {t("pages.forbidden.notEnabledNotice")}
            </p>
          </>
        ) : (
          <>
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 fx-glow">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-3xl font-bold">{t("pages.forbidden.title")}</h1>
            <p className="text-base text-muted-foreground">
              {t("pages.forbidden.noticePrefix")}{" "}
              <code className="rounded bg-muted-foreground/10 px-1.5 py-0.5 text-xs">
                {user.roleLabel ?? user.role}
              </code>{" "}
              {t("pages.forbidden.noticeSuffix")}
            </p>
            <p className="text-xs text-muted-foreground">{t("pages.forbidden.hint")}</p>
          </>
        )}
        <div className="flex items-center justify-center gap-3 pt-2">
          {expired && (
            <Button onClick={() => navigate("/works", { replace: true })}>去我的作品</Button>
          )}
          <Button variant="outline" onClick={() => navigate("/", { replace: true })}>
            {t("pages.forbidden.back")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
          >
            {t("pages.forbidden.relogin")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** 1005:因到期关闭的舱。单拆组件:只有这一支要读订阅(useQuery),其它 403 分支不拉。 */
function ExpiredNotice() {
  const lapse = useSubscriptionLapse();
  const partnerTenant = isPartnerBrand(useBrand());
  return (
    <>
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
        <CalendarX className="h-8 w-8 text-destructive" />
      </div>
      <h1 className="text-2xl font-bold" data-testid="forbidden-expired-title">
        {lapse.suspended ? "套餐已暂停使用" : lapse.expired ? "套餐已到期" : "该功能已到期"}
      </h1>
      <p className="text-base text-muted-foreground">
        {lapse.expired
          ? lapseNoticeText(lapse, partnerTenant)
          : `该功能的使用期已结束,历史作品仍可在「我的作品」查看和下载。续费请联系您的${
              partnerTenant ? "服务商" : "客户经理"
            }。`}
      </p>
    </>
  );
}
